import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import dateUtils from '../../../util/dates';
import dataUtils from '../../../util/dataUtils';
import { isWhite } from '../../../util/colors';
import { escapeHTML } from '../../../util/text';

import highchartRegression from '../../../util/highcharts-regression';
import highchartFactory from '../../../util/highcharts';
import { formatWorkDuration } from '../../../util/dates';
const highchartUtils = highchartFactory(Highcharts);

const chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'scatter',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 50,
    marginLeft: 70,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  time: {
    useUTC: false
  },
  xAxis: {
    type: 'datetime',
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    tickInterval: 24 * 3600 * 1000,
    tickmarkPlacement: 'on',
    title: {
      enabled: false
    }
  },
  yAxis: {
    min: 0,
    opposite: false,
    allowDecimals: false,
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Cycle time',
      style: {
        fontSize: '13px'
      }
    },
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    plotLines: []
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      stickyTracking: false,
      cursor: 'pointer',
      point: {
        events: {}
      }
    },
    scatter: {
      marker: {
        symbol: 'circle',
        radius: 7,
        states: {
          hover: {
            lineColor: 'rgb(100,100,100)'
          }
        }
      },
      states: {
        hover: {
          marker: {
            enabled: false
          }
        }
      }
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0,
    formatter: function() {}
  },
  series: []
};

export class CTSPChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      config: {}
    };
  }

  static defaultProps = {
    onPointClick: function() {}
  };

  calculatePercentiles = data => {
    const lines = [],
      ticks = [];
    let lastValue = 0;

    let maxValue = 0;
    if (!!data.series) {
      const last = _(data.series)
        .flatMap('data')
        .orderBy('y')
        .last();
      maxValue = !!last ? last.y : 0;
    }

    for (let i = this.props.percentiles.length - 1; i >= 0; i--) {
      const pObj = _.find(data.percentiles, item => item.name == this.props.percentiles[i]);
      if (!pObj) {
        continue;
      }

      const text = `${pObj.name}%`;
      const value = Math.ceil(moment.duration(pObj.value, 'day').as(this.props.timeUnit));

      const diff = lastValue - value;
      let labelY = -4,
        tickY = 6;
      if (diff > 0 && diff < maxValue * 0.02) {
        tickY = 12;
        labelY = 12;
      }

      lines.push({
        color: '#444444',
        dashStyle: 'dot',
        width: 2,
        value,
        label: {
          align: 'right',
          className: 'highcharts-plot-line-label-right',
          text,
          x: 45,
          y: 5,
          useHTML: true,
          style: {
            ...highchartUtils.getFontSizeConfig('percentile', this.props.wallmode),
          }
        }
      });

      if (_.findIndex(ticks, { value }) === -1) {
        ticks.push({
          color: '#444444',
          dashStyle: 'dot',
          width: 2,
          zIndex: 2,
          value,
          label: {
            textAlign: 'right',
            x: -9,
            y: tickY,
            text: `${value}`,
            useHTML: true,
            style: {
              ...highchartUtils.getFontSizeConfig('percentile', this.props.wallmode),
            }
          }
        });
        ticks.push({
          dashStyle: 'line',
          color: 'transparent',
          width: 4,
          zIndex: 2,
          value,
          events: highchartUtils.plotlinesTooltip(text)
        });
      }

      lastValue = value;
    }

    const newLines = [];
    _(lines)
      .groupBy('value')
      .forEach(l => {
        const line = l[0];
        const labels = _.map(l, i => {
          return i.label.text;
        });
        line.label.text = labels.join(', ');
        newLines.push(line);
      });

    return newLines.concat(ticks);
  };

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);
    highchartRegression(Highcharts);

    const that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(that, this);
    };

    chartConfig.plotOptions.series.point.events.click = function() {
      that.onPointClick(that, this);
    };

    chartConfig.exporting = this.props.chartExporting;

    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);

    if (this.props.data.series) {
      this.updateChart();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.updateChart(prevProps.data);
    }

    if (prevProps.percentiles != this.props.percentiles) {
      this.chart.update({
        yAxis: {
          plotLines: this.calculatePercentiles(this.props.data)
        }
      });
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (prevProps.timeUnit != this.props.timeUnit) {
      let obj = {
        yAxis: {
          title: {
            text: `Cycle time (${this.props.timeUnit})`
          }
        }
      };
      this.chart.update(obj);
    }

    if (prevProps.showNavigator != this.props.showNavigator) {
      this.chart.reflow();
    }

    if (prevProps.trendLine != this.props.trendLine) {
      this.updateRegression();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename,
            chartOptions: {
              title: {
                text: 'Cycle Time Scatterplot',
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              },
            }
          }
        },
        false
      );
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChart(oldData = {}) {
    this.chart.xAxis[0].setExtremes(null, null);

    const diff = dataUtils.getDiff(oldData.series, this.props.data.series, 'data');
    highchartUtils.updateSeries(diff, this.chart);
    const xExtremes = this.getExtremes();
    this.chart.update(
      {
        xAxis: {
          tickInterval: xExtremes.days * 24 * 3600 * 1000
        },
        yAxis: {
          title: {
            text: `Cycle time (${this.props.timeUnit})`
          },
          plotLines: this.calculatePercentiles(this.props.data)
        }
      },
      false
    );

    this.setExtremes(xExtremes);
    this.chart.redraw();
    this.updateRegression();
  }

  updateRegression() {
    const trend = _.find(this.chart.series, { name: 'trend' });
    if (trend) {
      this.chart.series[trend.index].remove(true);
    }
    if (this.props.trendLine) {
      const data = _([...this.props.data.series])
        .flatMap('data')
        .sortBy('x')
        .value();

      this.chart.addSeries(
        {
          data,
          visible: false,
          regression: true,
          regressionSettings: {
            name: 'trend',
            index: 0,
            useAllSeries: true,
            type: 'polynomial',
            order: 6,
            color: 'rgb(87, 181, 70)',
            lineWidth: 3,
            dashStyle: 'LongDashDot'
          }
        },
        true
      );
    }
  }

  updateTimeLabelsFormat(dateFormat) {
    let config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.chart.update(config);
  }

  updateColors(colored) {
    if (colored) {
      const updateSeriesColor = (chart, serie) => {
        const index = _.findIndex(chart.series, {
          name: serie.name,
          color: serie.color
        });
        if (!!chart.series[index]) {
          chart.series[index].update({
            color: serie.color
          });
        }
      };

      for (let i = 0; i < this.props.data.series.length; i++) {
        const serie = this.props.data.series[i];
        updateSeriesColor(this.chart, serie);
      }
    } else {
      for (let i = 0; i < this.chart.series.length; i++) {
        this.chart.series[i].update({
          color: 'rgb(124,181,236)'
        });
      }
    }
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('ctsp', this.props.wallmode);
    this.chart.update(newConfig.chart);
  }

  getExtremes() {
    let minVal, maxVal;

    const sorted = _([...this.props.data.series])
      .flatMap('data')
      .orderBy('x')
      .map('x');
    minVal = sorted.first();
    maxVal = sorted.last();

    if (moment(minVal).isSameOrAfter(this.props.dates.startDate)) {
      minVal = this.props.dates.startDate;
    }
    if (moment(maxVal).isSameOrAfter(this.props.dates.endDate)) {
      maxVal = this.props.dates.endDate;
    }

    switch (this.props.timeUnit) {
      case 'week': {
        minVal = +moment(minVal).startOf('week');
        maxVal = +moment(new Date(maxVal).endOf('week')).add(2, 'days');
        break;
      }
      case 'month': {
        minVal = +moment(minVal).date(1);
        maxVal = +moment(maxVal).date(30);
        break;
      }
      default: {
        minVal = +minVal;
        maxVal = +maxVal;
        break;
      }
    }

    return {
      min: minVal,
      max: maxVal,
      days:  Math.max(moment(maxVal).diff(moment(minVal), 'days') / 10, 1)
    };
  }

  setExtremes(xExtremesObject, yExtremesObject) {
    if (xExtremesObject) {
      this.chart.xAxis[0].setExtremes(xExtremesObject.min, xExtremesObject.max);
    }
    if (yExtremesObject) {
      this.chart.yAxis[0].setExtremes(yExtremesObject.min, yExtremesObject.max);
    } else {
      this.chart.yAxis[0].setExtremes(null, null);
    }
  }

  getDateString(date) {
    let str = moment(date).format(this.props.dateFormat);
    if (this.props.timeUnit === 'week') {
      str = `${moment(date)
        .startOf('isoWeek')
        .format(this.props.dateFormat)} - ${moment(date)
        .endOf('isoWeek')
        .format(this.props.dateFormat)}`;
    } else if (this.props.timeUnit === 'month') {
      str = `${moment(date)
        .startOf('month')
        .format(this.props.dateFormat)} - ${moment(date)
        .endOf('month')
        .format(this.props.dateFormat)}`;
    }

    return str;
  }

  onPointClick(comp, ev) {
    const points = [];
    const date = comp.getDateString(ev.x);
    const duration = formatWorkDuration(
      ev.cycleTimeInMinutes,
      comp.props.excludeWeekendOpts,
      comp.props.timePrecision,
      comp.props.useWorkingHoursInCycleTimeDays
    );

    for (let s = 0; s < comp.chart.series.length; s++) {
      for (let i = 0; i < comp.chart.series[s].data.length; i++) {
        const point = comp.chart.series[s].data[i];
        if (moment(point.x).isSame(ev.x, 'hour') && point.y == ev.y && point.card) {
          points.push({
            card: point.card,
            details: point.details,
            color: point.color
          });
        }
      }
    }

    comp.props.onPointClick({
      date,
      value: duration,
      points
    });
  }

  tooltipFormatter(comp, ev) {
    if (ev.series.name === 'trend') {
      return '<div></div>';
    }

    if (ev.key == 'Plotline') {
      return `${ev.point.text}: ${ev.y} ${comp.props.timeUnit}s`;
    }

    const { selectedDate } = this.props;

    let date = comp.getDateString(ev.x);
    const duration = formatWorkDuration(
      ev.point.cycleTimeInMinutes,
      comp.props.excludeWeekendOpts,
      comp.props.timePrecision,
      comp.props.useWorkingHoursInCycleTimeDays
    );
    let tooltip = `<p class="tooltip-label-title-size" style="margin: 8px 8px 0;">${_.upperFirst(
      selectedDate
    )} date: ${date}</p> <p class="tooltip-label-size" style="margin: 8px 10px 0;">Cycle time: ${duration}</p>`;

    for (let s = 0; s < comp.chart.series.length; s++) {
      for (let i = 0; i < comp.chart.series[s].data.length; i++) {
        let point = comp.chart.series[s].data[i];
        if (point.card && moment(point.x).isSame(ev.x, 'hour') && point.y == ev.y) {
          const isBgWhite = isWhite(point.color) ? 'border: 1px solid #00408026;' : '';
          tooltip += `<div class="tooltip-list-item"><div class="tooltip-circle-dot" style="${isBgWhite} background: ${point.color}"></div>${escapeHTML(point.card.name)}</div>`;
        }
      }
    }

    tooltip += `<p class="tooltip-info-txt">Click on the bar to see the details</p>`;

    return tooltip;
  }

  render() {
    return (
      <div className="chart_content chart_tooltip_style">
        <div id={`${this.props.id}-details`} className={'chart_details chart_details-full'}></div>
      </div>
    );
  }
}

CTSPChart.propTypes = {
  onPointClick: PropTypes.func
};

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.dateFormat,
    timePrecision: state.filters.timePrecision,
    percentiles: state.filters.ctsp.selectedPercentiles,
    timeUnit: state.filters.ctsp.timeUnit,
    trendLine: state.filters.ctsp.trendLine,
    selectedDate: state.filters.ctsp.selectedDate,
    wallmode: state.app.wallmode,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default withChartExport(connect(mapStateToProps)(CTSPChart));
